import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { setupAxiosRefreshInterceptor } from '../lib/axiosRefreshInterceptor';

const AuthContext = createContext({});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_TOKEN_KEY = 'refresh_token';

// Default permissions for trainers
const DEFAULT_PERMISSIONS = {
  can_view_calendar: true,
  can_edit_own_appointments: true,
  can_edit_all_appointments: false,
  can_view_customers: false,
  can_view_own_customers: true,
  can_edit_customers: false,
  can_view_finances: false,
  can_create_invoices: false,
  can_view_reports: false,
  can_view_own_reports: true,
  can_view_packages: false,
  can_sell_packages: false,
  can_view_settings: false,
  can_edit_settings: false,
};

// Admin has all permissions
const ADMIN_PERMISSIONS = Object.keys(DEFAULT_PERMISSIONS).reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  // Support-Login (Impersonation): aus localStorage wiederherstellen (Reload-fest)
  const [impersonating, setImpersonating] = useState(() => {
    try { return JSON.parse(localStorage.getItem('imp_info')) || null; } catch { return null; }
  });

  // Iter 329g (Phase 2): Refresh-Token-Interceptor einmalig installieren.
  // Beim 401 versucht er auto-refresh; bei Fehlschlag → logout().
  useEffect(() => {
    setupAxiosRefreshInterceptor({
      getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
      getAccessToken: () => localStorage.getItem('token'),
      onRefreshSuccess: (data) => {
        const newAt = data.access_token;
        const newRt = data.refresh_token;
        if (newAt) {
          localStorage.setItem('token', newAt);
          // Customer-Portal nutzt einen separaten Key — auch updaten
          if (data?.user?.role === 'customer') {
            localStorage.setItem('customerToken', newAt);
          }
          axios.defaults.headers.common['Authorization'] = `Bearer ${newAt}`;
          setToken(newAt);
        }
        if (newRt) {
          localStorage.setItem(REFRESH_TOKEN_KEY, newRt);
        }
      },
      onRefreshFail: () => {
        // Refresh hat versagt → klassischer Logout (clearen + redirect erfolgt
        // weiter unten durch React-Router-Wachen wenn user=null)
        localStorage.removeItem('token');
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem('customerToken');
        setToken(null);
        setUser(null);
        setPermissions(null);
        delete axios.defaults.headers.common['Authorization'];
      },
    });
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Load permissions for this user
      await fetchPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Token wirklich ungültig → ausloggen
        logout();
      } else {
        // Netzwerk-/Serverfehler (z.B. Deploy): Session behalten, später erneut versuchen
        setTimeout(fetchUser, 8000);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async (userData) => {
    try {
      // Admins have all permissions
      if (userData?.role === 'admin') {
        setPermissions(ADMIN_PERMISSIONS);
        return;
      }
      
      // Trainers have configurable permissions
      if (userData?.role === 'trainer') {
        const response = await axios.get(`${API}/users/me/permissions`);
        setPermissions(response.data.permissions || DEFAULT_PERMISSIONS);
        return;
      }
      
      // Customers/Mieter have minimal permissions
      setPermissions({});
    } catch (error) {
      console.error('Failed to fetch permissions', error);
      // Fallback to defaults for trainers
      if (userData?.role === 'trainer') {
        setPermissions(DEFAULT_PERMISSIONS);
      } else {
        setPermissions({});
      }
    }
  };

  const _finalizeLogin = async (data) => {
    const { access_token, refresh_token, user } = data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    }
    if (user?.role === 'customer') {
      localStorage.setItem('customerToken', access_token);
    }
    if (user?.role) localStorage.setItem('userRole', user.role);
    // Mehrbetrieb: Betriebsliste für den „Betrieb wechseln"-Umschalter merken.
    if (Array.isArray(data.businesses)) {
      localStorage.setItem('businesses', JSON.stringify(data.businesses));
    } else {
      localStorage.removeItem('businesses');
    }
    setToken(access_token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    await fetchPermissions(user);
    return user;
  };

  // Mehrbetrieb: aktiven Betrieb wechseln (neues Token, ohne Passwort).
  const switchBusiness = async (slug) => {
    const response = await axios.post(`${API}/auth/switch-business`, { slug });
    await _finalizeLogin(response.data);
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Super-Admin mit aktiver 2FA: kein Token, sondern Pending-Phase.
    if (response.data?.requires_totp) {
      return { requires_totp: true, pending_token: response.data.pending_token };
    }
    return _finalizeLogin(response.data);
  };

  // Zweiter Login-Schritt (Super-Admin 2FA): TOTP- oder Recovery-Code.
  const completeTotpLogin = async (pending_token, code) => {
    const response = await axios.post(`${API}/auth/login/totp`, { pending_token, code });
    return _finalizeLogin(response.data);
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    }
    setToken(access_token);
    setUser(user);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Load permissions after registration
    await fetchPermissions(user);
    
    return user;
  };

  const logout = async () => {
    // Iter 329g: dem Backend Bescheid geben damit der Refresh-Token serverseitig
    // revoked wird. Best-effort — bei Netzfehler trotzdem lokal logout.
    const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (rt) {
      try {
        await axios.post(`${API}/auth/logout`, { refresh_token: rt }, { timeout: 3000 });
      } catch (e) {
        // ignore — Logout-Endpoint ist idempotent, fail-safe
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('customerToken');
    setToken(null);
    setUser(null);
    setPermissions(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  /**
   * Iter 329h: Meldet ALLE anderen Sessions des Users ab (z. B. Phone verloren).
   * Aktuelles Gerät bleibt eingeloggt — der eigene Refresh-Token wird
   * via `keep_current_refresh_token` ausgeschlossen.
   *
   * Returns: Anzahl revokter Sessions (=> "X andere Geräte abgemeldet").
   */
  const logoutOtherDevices = async () => {
    const rt = localStorage.getItem(REFRESH_TOKEN_KEY);
    const { data } = await axios.post(
      `${API}/auth/logout-all`,
      { keep_current_refresh_token: rt || undefined },
    );
    return data?.revoked_count ?? 0;
  };

  /**
   * Setzt einen bereits ausgestellten Access-Token (z.B. nach OTP-Login
   * via /auth/otp/verify oder Magic-Link). Holt anschließend das
   * vollständige User-Profil und lädt die Permissions.
   *
   * Iter 329g: akzeptiert auch optional einen Refresh-Token für die
   * neue Token-Rotation. Magic-Link-Pages und OTP-Verify-Pages sollten
   * diesen weitergeben sobald der Server ihn liefert.
   */
  const loginWithToken = async (accessToken, refreshToken = null) => {
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    setToken(accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const { data } = await axios.get(`${API}/auth/me`);
    setUser(data);
    await fetchPermissions(data);
    return data;
  };

  /**
   * Support-Login (Impersonation): startet eine zeitlich begrenzte Sitzung
   * als Zielnutzer. Die aktuelle Super-Admin-Session wird gesichert und beim
   * Beenden wiederhergestellt. Während des Support-Modus wird der Refresh-Token
   * entfernt, damit der Interceptor nicht still in die Super-Admin-Session
   * zurückrefresht.
   */
  const startImpersonation = async (data) => {
    const origToken = localStorage.getItem('token');
    const origRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    const info = { ...data.user, exp: Date.now() + (data.expires_in * 1000) };
    localStorage.setItem('imp_orig_token', origToken || '');
    if (origRefresh) localStorage.setItem('imp_orig_refresh', origRefresh);
    localStorage.setItem('imp_info', JSON.stringify(info));
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.setItem('token', data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
    setImpersonating(info);
    setToken(data.access_token); // triggert fetchUser → Zielnutzer + Permissions
    return info;
  };

  const stopImpersonation = async () => {
    let info = null;
    try { info = JSON.parse(localStorage.getItem('imp_info')); } catch (e) { info = null; }
    const origToken = localStorage.getItem('imp_orig_token');
    const origRefresh = localStorage.getItem('imp_orig_refresh');
    // Beenden protokollieren — mit Original-Super-Admin-Token (imp-Token darf das nicht)
    if (origToken && info) {
      try {
        await axios.post(
          `${API}/superadmin/support-login/end`,
          { user_id: info.id, user_email: info.email },
          { headers: { Authorization: `Bearer ${origToken}` } },
        );
      } catch (e) { /* best-effort */ }
    }
    localStorage.removeItem('imp_info');
    localStorage.removeItem('imp_orig_token');
    localStorage.removeItem('imp_orig_refresh');
    localStorage.removeItem('customerToken');
    setImpersonating(null);
    if (origToken) {
      if (origRefresh) localStorage.setItem(REFRESH_TOKEN_KEY, origRefresh);
      localStorage.setItem('token', origToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${origToken}`;
      setToken(origToken); // triggert fetchUser → Super-Admin wiederhergestellt
    } else {
      logout();
    }
  };

  // Support-Sitzung läuft nach Ablauf (30 Min) automatisch aus.
  useEffect(() => {
    if (!impersonating?.exp) return;
    const ms = impersonating.exp - Date.now();
    if (ms <= 0) { stopImpersonation(); return; }
    const t = setTimeout(() => { stopImpersonation(); }, ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonating]);

  // Helper to check a specific permission
  const hasPermission = (permissionKey) => {    // Admins always have permission
    if (user?.role === 'admin') return true;
    // Check specific permission
    return permissions?.[permissionKey] === true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      completeTotpLogin,
      register, 
      logout, 
      logoutOtherDevices,
      loginWithToken,
      switchBusiness,
      startImpersonation,
      stopImpersonation,
      impersonating,
      token,
      permissions,
      hasPermission 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
